import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { useHistory, useLocation } from 'react-router'
import { ColorRing } from 'react-loader-spinner'

import './Make.css'

import MakeHeader from '../ReusableComponents/Header/MakeHeader'
import renderImage from '../../source/importImg'
import getBackendImage from '../../source/imageUtil'
import { EXPANSION_KITS } from '../../utils/expansionKits'
import contentApi, { USE_NEW_API } from '../../utils/contentApi'

const BACKEND_URL = process.env.REACT_APP_API_BASE

const isJwtExpired = (token) => {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]))
        return payload.exp * 1000 < Date.now()
    } catch {
        return true
    }
}

const Make = (props) => {
    const history = useHistory()
    const location = useLocation()
    const device = sessionStorage.getItem('allowedDevice') || 'Toki'
    const deviceVersion = sessionStorage.getItem('deviceVersion')?.split('.')?.slice(0, 2)?.join('.') || '1.0'
    // When entered via an expansion kit, only show subprograms tagged with that kit
    const expansionKit = sessionStorage.getItem('expansionKit')
    const expansionKitName =
        sessionStorage.getItem('expansionKitName') ||
        EXPANSION_KITS[expansionKit]?.name ||
        expansionKit

    const [program, setProgram] = useState(null)
    const [loading, setLoading] = useState(true)
    const [completed, setCompleted] = useState([])

    // State for the guest warning banner
    const [showGuestBanner, setShowGuestBanner] = useState(false)

    // Check auth status on mount and periodically so the banner appears when JWT expires
    useEffect(() => {
        const checkAuthStatus = () => {
            const userJwt = localStorage.getItem('userJWT')

            if (userJwt && isJwtExpired(userJwt)) {
                // JWT just expired – clean up and force the banner
                localStorage.removeItem('userJWT')
                sessionStorage.removeItem('guestWarningDismissed')
                setShowGuestBanner(true)
                return
            }

            const warningDismissed = sessionStorage.getItem(
                'guestWarningDismissed'
            )
            if (!userJwt && !warningDismissed) {
                setShowGuestBanner(true)
            }
        }

        checkAuthStatus()
        const interval = setInterval(checkAuthStatus, 10000)
        return () => clearInterval(interval)
    }, [])

    // NEW: Handlers for the banner buttons
    const handleDismissWarning = () => {
        sessionStorage.setItem('guestWarningDismissed', 'true')
        setShowGuestBanner(false)
    }

    const handleGoToLogin = () => {
        history.push('/login')
    }

    useEffect(() => {
        // If navigating back from a completed subprogram, apply the badge immediately
        // (before the DB fetch resolves, to avoid any race condition)
        const justCompleted = location.state?.justCompleted
        if (justCompleted) {
            setCompleted((prev) =>
                prev.includes(justCompleted) ? prev : [...prev, justCompleted]
            )
        }

        // Fetch the authoritative completed list from the backend
        const token = localStorage.getItem('userJWT')
        if (token) {
            fetch(`${BACKEND_URL}/api/progress/all`, {
                headers: { Authorization: `Bearer ${token}` },
                credentials: 'include',
            })
                .then((res) => (res.ok ? res.json() : null))
                .then((data) => {
                    if (data && Array.isArray(data.completed)) {
                        setCompleted((prev) => [
                            ...new Set([...prev, ...data.completed]),
                        ])
                    }
                })
                .catch((err) =>
                    console.error('Failed to fetch completed subprograms', err)
                )
        }
    }, [])

    useEffect(() => {
        let buffer = ''
        let lastKeyTime = Date.now()

        const handleKeyDown = (e) => {
            // Use userAgent as navigator.platform is technically deprecated
            const isMac = navigator.userAgent.toUpperCase().includes('MAC')

            // PRIMARY: Cmd + Shift + Y (Mac) or Ctrl + Shift + Y (Windows/Linux)
            const isMacShortcut =
                isMac && e.metaKey && e.shiftKey && e.key.toLowerCase() === 'y'
            const isWinShortcut =
                !isMac && e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 'y'

            if (isMacShortcut || isWinShortcut) {
                e.preventDefault()
                history.push('/makeAdmin')
                return
            }

            // RESET buffer if user pauses typing (3s gap)
            const now = Date.now()
            if (now - lastKeyTime > 3000) {
                buffer = ''
            }
            lastKeyTime = now

            // BACKUP: "admin" sequence
            // We ensure we are only buffering single character presses
            if (e.key.length === 1) {
                buffer += e.key.toLowerCase()

                if (buffer.includes('admin')) {
                    history.push('/admin')
                    buffer = ''
                }

                // keep buffer small
                if (buffer.length > 10) {
                    buffer = buffer.slice(-10)
                }
            }
        }

        window.addEventListener('keydown', handleKeyDown)

        return () => {
            window.removeEventListener('keydown', handleKeyDown)
        }
    }, [history])

    useEffect(() => {
        ;(USE_NEW_API
            ? contentApi.getProgramSubprograms(device.toLowerCase())
            : fetch(`${BACKEND_URL}/api/program/${device.toLowerCase()}`).then(
                  (res) => res.json()
              )
        )
            .then((data) => {
                setProgram(data)
                setLoading(false)
            })
            .catch((err) => {
                console.error('Failed to load program:', err)
                setLoading(false)
            })
    }, [])

    const goBack = () => {
        history.push('/kitMenu')
    }

    const openSubprogram = (subId) => {
        if (props.isConnecting) return
        history.push(`/make/${subId}`)
    }

    // Filter to the selected expansion kit's subprograms; otherwise show all
    const visibleSubprograms = (program?.subprograms || []).filter(
        (sub) => !expansionKit || sub.kit === expansionKit
    )

    return (
        <>
            <div className="Main_Select">
                <div className="Select_Headers">
                    <MakeHeader
                        title={expansionKit ? expansionKitName : `${device} (v${deviceVersion})`}
                        titlePosition="left"
                        help_btnPosition="right"
                        isGoBack={true}
                        goBack={goBack}
                        showAccountSetting={true}
                        showHelp={true}
                        showBluetoothBtn={true}
                    />
                </div>

                <div className="TokiKits_Body">
                    {loading && (
                        <div
                            style={{
                                gridColumn: '1 / -1',
                                textAlign: 'center',
                                padding: '40px',
                            }}
                        >
                            {/* <ColorRing
                                visible={true}
                                height="60"
                                width="60"
                                colors={[
                                    '#e15b64',
                                    '#f47e60',
                                    '#f8b26a',
                                    '#abbd81',
                                    '#849b87',
                                ]}
                            /> */}
                        </div>
                    )}

                    {!loading &&
                        program &&
                        visibleSubprograms.map((sub, index) => {
                            const isCompleted = completed.includes(sub.id)
                            return (
                                <div
                                    key={sub.id}
                                    className={`TokiKit_Card ${isCompleted ? 'TokiKit_Completed' : ''}`}
                                    onClick={() => openSubprogram(sub.id)}
                                    style={{
                                        opacity: props.isConnecting ? 0.5 : 1,
                                    }}
                                >
                                    <div
                                        className="TokiKit_CardImg"
                                        style={{ position: 'relative' }}
                                    >
                                        <img
                                            src={getBackendImage(
                                                sub.image || 'projectDefault'
                                            )}
                                            alt={sub.title}
                                            className={
                                                isCompleted
                                                    ? 'TokiKit_Image_Completed'
                                                    : ''
                                            }
                                        />

                                        {isCompleted && (
                                            <div className="TokiKit_CompletedBadge">
                                                <svg
                                                    viewBox="0 0 24 24"
                                                    width="22"
                                                    height="22"
                                                >
                                                    <circle
                                                        cx="12"
                                                        cy="12"
                                                        r="12"
                                                        fill="#4caf50"
                                                    />
                                                    <path
                                                        d="M7 12l3 3 7-7"
                                                        fill="none"
                                                        stroke="white"
                                                        strokeWidth="2.5"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            </div>
                                        )}
                                    </div>

                                    <p
                                        className={`TokiKit_CardName ${isCompleted ? 'TokiKit_Text_Completed' : ''}`}
                                    >
                                        {sub.title}
                                    </p>
                                </div>
                            )
                        })}

                        {!loading && visibleSubprograms.length === 0 && (
            <div
                style={{
                    gridColumn: '1 / -1',
                    textAlign: 'center',
                    padding: '40px',
                    color: '#888',
                    fontSize: '1.1rem',
                }}
            >
                No make projects available yet.
            </div>
        )}
                </div>
            </div>

            <div className="loading-overlay">
                <ColorRing
                    visible={props.isConnecting}
                    height="80"
                    width="80"
                    ariaLabel="color-ring-loading"
                    colors={[
                        '#e15b64',
                        '#f47e60',
                        '#f8b26a',
                        '#abbd81',
                        '#849b87',
                    ]}
                />
            </div>

            <div className="loading-overlay">
                <ColorRing
                    visible={props.isConnecting}
                    height="80"
                    width="80"
                    ariaLabel="color-ring-loading"
                    colors={[
                        '#e15b64',
                        '#f47e60',
                        '#f8b26a',
                        '#abbd81',
                        '#849b87',
                    ]}
                />
            </div>

            {/* NEW: Guest Warning Banner */}
            {showGuestBanner && (
                <div className="GuestWarning_Banner">
                    <div className="GuestWarning_Text">
                        <svg
                            viewBox="0 0 24 24"
                            width="24"
                            height="24"
                            fill="#f15a29"
                        >
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                        </svg>
                        <p>
                            <strong>Not logged in.</strong> Your project
                            progress won't be saved.
                        </p>
                    </div>
                    <div className="GuestWarning_Actions">
                        <button
                            className="GuestBtn_Cancel"
                            onClick={handleDismissWarning}
                        >
                            Dismiss
                        </button>
                        <button
                            className="GuestBtn_Login"
                            onClick={handleGoToLogin}
                        >
                            Log In
                        </button>
                    </div>
                </div>
            )}
        </>
    )
}

const mapStateToProps = (state) => ({
    isConnecting: state.isConnecting,
    languageName: state.device.language,
})

export default connect(mapStateToProps)(Make)
